import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CircularProgress,
  Alert,
  IconButton,
  Grid,
  Chip,
  TextField,
  MenuItem,
  Button,
  Divider,
  Dialog,
  DialogContent,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Cancel,
  Send,
  Refresh,
  Comment as CommentIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { FiTrash2 } from 'react-icons/fi';
import { remindersAPI, donationsAPI, contactsAPI } from '../services/api';
import { showError, showSuccess } from '../utils/toast';
import { getTodayDate } from '../utils/dateUtils';
import { colors } from '../theme/modernTheme';
import { useAuth } from '../context/AuthContext';
import DonationConfirmationWrapper from '../components/DonationConfirmationWrapper';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import { getFrequencyLabel } from '../utils/frequencyLabel';

export default function ReminderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [reminder, setReminder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Comments state
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState('');
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const commentsEndRef = useRef(null);

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    reminder_date: '',
    next_recurring_date: '',
    recurring_end_date: '',
    pledge_end_date: '',
    recurring_status: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});

  const initialMode = id && id !== 'new' && searchParams.get('mode') !== 'edit' ? 'view' : 'edit';
  const [mode, setMode] = useState(initialMode);

  useEffect(() => {
    loadReminder();
  }, [id]);

  const loadReminder = async () => {
    try {
      setLoading(true);
      const response = await remindersAPI.getAll({ id });
      if (response.data && response.data.length > 0) {
        const reminderData = response.data[0];
        setReminder(reminderData);

        // Initialize edit form data
        setEditFormData({
          reminder_date: reminderData.reminder_date || '',
          next_recurring_date: reminderData.reminder_date || '',
          recurring_end_date: reminderData.recurring_end_date || '',
          pledge_end_date: reminderData.pledge_end_date || '',
          recurring_status: reminderData.status || '',
        });

        // Load comments for the contact associated with this reminder
        if (reminderData.contact_id) {
          loadComments(reminderData.contact_id);
        }
      } else {
        showError('Reminder not found');
        navigate('/reminders');
      }
    } catch (err) {
      showError(err?.error?.message || 'Failed to load reminder');
      navigate('/reminders');
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async (contactId) => {
    if (!contactId) return;

    setCommentsLoading(true);
    setCommentsError('');
    try {
      const response = await contactsAPI.getContactComments(contactId);
      setComments(response.data || []);
    } catch (error) {
      setCommentsError(error?.error?.message || 'Failed to load comments');
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!reminder?.contact_id) {
      showError('Cannot add comment: No contact associated with this reminder');
      return;
    }

    setSubmittingComment(true);
    try {
      const response = await contactsAPI.createContactComment(reminder.contact_id, {
        comment_text: newComment,
      });
      setComments([...comments, response.data]);
      setNewComment('');
      showSuccess('Comment added successfully');
      // Scroll to bottom after adding comment
      setTimeout(() => {
        commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      showError(error?.error?.message || 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!reminder?.contact_id) {
      showError('Cannot delete comment: No contact associated with this reminder');
      return;
    }

    try {
      await contactsAPI.deleteContactComment(reminder.contact_id, commentId);
      setComments(comments.filter((c) => c.id !== commentId));
      showSuccess('Comment deleted successfully');
    } catch (error) {
      showError(error?.error?.message || 'Failed to delete comment');
    }
  };

  const getDisplayValue = (value, type = 'text') => {
    if (!value && value !== 0) return '—';

    switch (type) {
      case 'date':
        // Handle both date strings and timestamps
        const dateStr = typeof value === 'string' ? value.split('T')[0].split(' ')[0] : value;
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('en-IN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      case 'time':
        return value;
      case 'currency':
        return `₹${parseFloat(value).toLocaleString()}`;
      default:
        return value;
    }
  };

  const getContactName = () => {
    if (!reminder) return '—';
    if (reminder.first_name) {
      return `${reminder.first_name} ${reminder.last_name || ''}`.trim();
    }
    return reminder.organization_name || '—';
  };

  const getReminderTypeLabel = (type) => {
    const labels = {
      FOLLOW_UP: 'Follow Up',
      THANK_YOU: 'Thank You',
      RECURRING: 'Recurring',
      PLEDGE: 'Pledge',
    };
    return labels[type] || type;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return colors.success.main;
      case 'PAUSED':
        return colors.warning.main;
      case 'CANCELLED':
      case 'COMPLETED':
        return colors.error.main;
      case 'SENT':
        return colors.success.main;
      case 'OVERDUE':
        return colors.error.main;
      default:
        return colors.text.secondary;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'FOLLOW_UP':
        return {
          bg: `${colors.primary.main}15`,
          color: colors.primary.main,
        };
      case 'THANK_YOU':
        return {
          bg: `${colors.success.main}15`,
          color: colors.success.main,
        };
      case 'RECURRING':
        return {
          bg: `${colors.info.main}15`,
          color: colors.info.main,
        };
      case 'PLEDGE':
        return {
          bg: `${colors.warning.main}15`,
          color: colors.warning.main,
        };
      default:
        return {
          bg: colors.action.hover,
          color: colors.text.secondary,
        };
    }
  };

  const handleEditClick = () => {
    setMode('edit');
    navigate(`/reminders/${id}?mode=edit`);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await remindersAPI.delete(id);
      showSuccess('Reminder deleted successfully');
      navigate('/reminders');
    } catch (error) {
      showError(error?.error?.message || 'Failed to delete reminder');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleCancelEdit = () => {
    setMode('view');
    navigate(`/reminders/${id}`, { replace: true });
    // Reset form data to original values
    setEditFormData({
      reminder_date: reminder.reminder_date || '',
      next_recurring_date: reminder.next_recurring_date || '',
      recurring_end_date: reminder.recurring_end_date || '',
      pledge_end_date: reminder.pledge_end_date || '',
      recurring_status: reminder.reminder_status || '',
    });
    setFieldErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Update the form data
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!editFormData.reminder_date) {
      errors.reminder_date = 'Reminder date is required';
    }

    if (!editFormData.next_recurring_date) {
      errors.next_recurring_date = 'Next recurring date is required';
    }

    if (!editFormData.recurring_status) {
      errors.recurring_status = 'Status is required';
    }

    // Validate that reminder_date is not after pledge_end_date
    if (editFormData.reminder_date && editFormData.pledge_end_date) {
      const reminderDate = new Date(editFormData.reminder_date);
      const endDate = new Date(editFormData.pledge_end_date);

      if (reminderDate > endDate) {
        errors.reminder_date = 'Reminder date cannot be after the pledge end date';
      }
    }

    // Validate that next_recurring_date is not after pledge_end_date
    if (editFormData.next_recurring_date && editFormData.pledge_end_date) {
      const nextDate = new Date(editFormData.next_recurring_date);
      const endDate = new Date(editFormData.pledge_end_date);

      if (nextDate > endDate) {
        errors.next_recurring_date = 'Next recurring date cannot be after the pledge end date';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveClick = () => {
    // Validate form before saving
    if (!validateForm()) {
      // Don't show toast - field errors are displayed below each field
      return;
    }

    // If there's a donation associated, show confirmation dialog
    if (reminder.donation_id) {
      setConfirmDialogOpen(true);
    } else {
      // No donation associated, save directly
      handleSave();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setConfirmDialogOpen(false);

    try {
      // Update the reminder with all fields - the backend will now automatically sync with donation
      const reminderUpdates = {
        next_pledge_date: editFormData.reminder_date, // This updates reminder_date in DB (not reminder_date_backup)
      };

      // If recurring_end_date changed, include it
      if (editFormData.recurring_end_date !== (reminder.recurring_end_date || '')) {
        reminderUpdates.recurring_end_date = editFormData.recurring_end_date || null;
      }

      // Note: pledge_end_date is read-only and cannot be edited from reminders

      // If status changed, include it in the reminder update
      if (
        editFormData.recurring_status &&
        editFormData.recurring_status !== reminder.reminder_status
      ) {
        reminderUpdates.status = editFormData.recurring_status;
      }

      // Update the reminder - backend will automatically sync with donation
      await remindersAPI.update(id, reminderUpdates);

      showSuccess('Reminder updated successfully');
      setMode('view');
      navigate(`/reminders/${id}`, { replace: true });
      // Reload reminder data
      await loadReminder();
    } catch (err) {
      // Handle insufficient amount error
      if (err.error?.code === 'INSUFFICIENT_AMOUNT') {
        showError(err.error.message);
        setFieldErrors({
          recurring_status: 'Cannot mark as completed until full amount is collected',
        });
      }
      // Handle validation errors
      else if (err.error?.code === 'VALIDATION_ERROR') {
        const errors = err.error.details?.field_errors || {};
        setFieldErrors(errors);
        // Don't show toast - field errors are displayed below each field
      } else {
        showError(err?.error?.message || 'Failed to update reminder');
      }
    } finally {
      setSaving(false);
    }
  };

  const canEdit = user?.role === 'ADMIN' || user?.role === 'STAFF';
  const canDelete = user?.role === 'ADMIN';
  const isViewMode = mode === 'view';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // if (error || !reminder) {
  //   return (
  //     <Box>
  //       <Box display="flex" alignItems="center" mb={3}>
  //         <IconButton
  //           onClick={() => navigate("/reminders")}
  //           size="small"
  //           sx={{
  //             mr: 2,
  //             "&:hover": { color: "rgba(0, 0, 0, 1)" },
  //           }}>
  //           <ArrowBack fontSize="small" />
  //         </IconButton>
  //         <Typography
  //           variant="h6"
  //           sx={{
  //             fontSize: "1.2rem",
  //             fontWeight: 700,
  //             color: "#000000",
  //           }}>
  //           Reminder Details
  //         </Typography>
  //       </Box>
  //     </Box>
  //   );
  // }

  return (
    <Box
      sx={{
        minHeight: '100vh',
      }}
    >
      {isViewMode ? (
        <Box>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <IconButton
                onClick={() => navigate('/reminders')}
                size="small"
                sx={{
                  '&:hover': { color: 'rgba(0, 0, 0, 1)' },
                }}
              >
                <ArrowBack fontSize="small" />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: '#000000',
                }}
              >
                Reminder Details
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {reminder?.status === 'PENDING' && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/transactions/new?reminder_id=${reminder.id}`)}
                  sx={{
                    borderRadius: '4px',
                    textTransform: 'none',
                    borderColor: colors.primary.main,
                    color: colors.primary.main,
                    '&:hover': {
                      borderColor: colors.primary.dark,
                      backgroundColor: `${colors.primary.main}10`,
                    },
                  }}
                >
                  Add Transaction
                </Button>
              )}
              {canEdit && (
                <Button
                  variant="text"
                  size="small"
                  onClick={handleEditClick}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '4px',
                    border: `1px solid ${colors.primary.main}`,
                    color: colors.primary.main,
                    '&:hover': {
                      borderColor: colors.primary.dark,
                      textDecoration: 'none',
                      backgroundColor: `${colors.action.hover} !important`,
                    },
                  }}
                >
                  Edit
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="text"
                  size="small"
                  onClick={handleDeleteClick}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '4px',
                    border: `1px solid ${colors.error.main}`,
                    color: colors.error.main,
                    '&:hover': {
                      borderColor: colors.error.dark,
                      textDecoration: 'none',
                      backgroundColor: `${colors.action.error} !important`,
                    },
                  }}
                >
                  Delete
                </Button>
              )}
            </Box>
          </Box>

          {/* Single Card - Full Width */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: '4px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  p: 3,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: colors.primary.main,
                    mb: 2,
                    pb: 1,
                    borderBottom: `2px solid ${colors.primary.main}`,
                  }}
                >
                  Reminder Information
                </Typography>

                <Grid container spacing={3}>
                  {/* Left Column */}
                  <Grid item xs={12} md={6}>
                    <Box display="flex" flexDirection="column" gap={2}>
                      {/* Contact */}
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            fontSize: '0.7rem',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Contact
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.text.primary,
                            fontWeight: 600,
                            mt: 0.5,
                          }}
                        >
                          {getContactName()}
                        </Typography>
                      </Box>

                      {/* Email */}
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            fontSize: '0.7rem',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Email
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.text.primary,
                            fontWeight: 500,
                            mt: 0.5,
                          }}
                        >
                          {getDisplayValue(reminder.primary_email)}
                        </Typography>
                      </Box>

                      {/* Phone Number */}
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            fontSize: '0.7rem',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Phone Number
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.text.primary,
                            fontWeight: 500,
                            mt: 0.5,
                          }}
                        >
                          {getDisplayValue(reminder.primary_number)}
                        </Typography>
                      </Box>

                      {/* Amount */}
                      {reminder.amount && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Amount
                          </Typography>
                          <Box display="flex" alignItems="baseline" gap={1} mt={0.5}>
                            <Typography
                              variant="h5"
                              sx={{
                                color: colors.text.primary,
                                fontWeight: 700,
                              }}
                            >
                              {getDisplayValue(reminder.amount, 'currency')}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {/* Donation Schema */}
                      {reminder.donation_schema_name && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Donation Scheme
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.text.primary,
                              fontWeight: 600,
                              mt: 0.5,
                            }}
                          >
                            {reminder.donation_schema_name}
                          </Typography>
                        </Box>
                      )}

                      {/* Trust Name */}
                      {reminder.trust_name && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Trust
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.text.primary,
                              fontWeight: 600,
                              mt: 0.5,
                            }}
                          >
                            {reminder.trust_name}
                          </Typography>
                        </Box>
                      )}

                      {/* Purpose */}
                      {reminder.purpose && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Purpose
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.text.primary,
                              mt: 0.5,
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {reminder.purpose}
                          </Typography>
                        </Box>
                      )}

                      {/* Notes */}
                      {reminder.notes && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Notes
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.text.primary,
                              mt: 0.5,
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {reminder.notes}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  {/* Right Column */}
                  <Grid item xs={12} md={6}>
                    <Box display="flex" flexDirection="column" gap={2}>
                      {/* Frequency */}
                      {reminder.frequency && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Frequency
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.text.primary,
                              fontWeight: 500,
                              mt: 0.5,
                            }}
                          >
                            {getFrequencyLabel(reminder.frequency)}
                          </Typography>
                        </Box>
                      )}

                      {/* Status */}
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            fontSize: '0.7rem',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Status
                        </Typography>
                        <Box mt={0.5}>
                          <Chip
                            label={reminder.status}
                            size="small"
                            sx={{
                              backgroundColor:
                                reminder.status === 'PENDING'
                                  ? `${colors.info.main}15`
                                  : reminder.status === 'COMPLETED'
                                    ? `${colors.success.main}15`
                                    : reminder.status === 'OVERDUE'
                                      ? `${colors.error.main}15`
                                      : colors.action.hover,
                              color:
                                reminder.status === 'PENDING'
                                  ? colors.info.main
                                  : reminder.status === 'COMPLETED'
                                    ? colors.success.main
                                    : reminder.status === 'OVERDUE'
                                      ? colors.error.main
                                      : colors.text.secondary,
                              fontWeight: 500,
                              fontSize: '0.75rem',
                              borderRadius: '4px',
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Reminder Start Date */}
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            fontSize: '0.7rem',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Reminder Date
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.text.primary,
                            fontWeight: 500,
                            mt: 0.5,
                          }}
                        >
                          {getDisplayValue(reminder.reminder_date, 'date')}
                        </Typography>
                      </Box>

                      {/* Next Recurring Date */}
                      {reminder.next_pledge_date && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Next Pledge Date
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.text.primary,
                              fontWeight: 500,
                              mt: 0.5,
                            }}
                          >
                            {getDisplayValue(reminder.next_pledge_date, 'date')}
                          </Typography>
                        </Box>
                      )}

                      {/* Recurring End Date */}
                      {reminder.recurring_end_date && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Recurring End Date
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.text.primary,
                              fontWeight: 500,
                              mt: 0.5,
                            }}
                          >
                            {getDisplayValue(reminder.recurring_end_date, 'date')}
                          </Typography>
                        </Box>
                      )}

                      {/* Pledge End Date */}
                      {reminder.pledge_end_date && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Pledge End Date
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.text.primary,
                              fontWeight: 500,
                              mt: 0.5,
                            }}
                          >
                            {getDisplayValue(reminder.pledge_end_date, 'date')}
                          </Typography>
                        </Box>
                      )}
                      {/* Pledge End Date */}
                      {reminder.recurring_amount && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Reminder Amount
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.text.primary,
                              fontWeight: 900,
                              mt: 0.5,
                            }}
                          >
                            {getDisplayValue(reminder.recurring_amount, 'currency')}
                          </Typography>
                        </Box>
                      )}

                      {/* Campaign */}
                      {reminder.campaign_name && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Campaign
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.text.primary,
                              fontWeight: 500,
                              mt: 0.5,
                            }}
                          >
                            {reminder.campaign_name}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            {/* Comments Section */}
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: '4px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  height: '600px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ p: 3, pb: 0 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: colors.primary.main,
                      mb: 2,
                      pb: 1,
                      borderBottom: `2px solid ${colors.primary.main}`,
                    }}
                  >
                    Comments
                  </Typography>
                </Box>

                {commentsLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
                    <CircularProgress />
                  </Box>
                ) : commentsError ? (
                  <Box p={3}>
                    <Alert
                      severity="error"
                      action={
                        <IconButton size="small" onClick={() => loadComments(reminder?.contact_id)}>
                          <Refresh />
                        </IconButton>
                      }
                    >
                      {commentsError}
                    </Alert>
                  </Box>
                ) : (
                  <>
                    {/* Comments Feed */}
                    <Box
                      sx={{
                        flex: 1,
                        overflowY: 'auto',
                        p: 3,
                        backgroundColor: '#ffffff',
                      }}
                    >
                      {comments.length === 0 ? (
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                          height="100%"
                        >
                          <CommentIcon sx={{ fontSize: 64, color: '#ddd', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                            No comments yet
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Start the conversation by adding a comment below
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          {comments.map((comment, index) => (
                            <React.Fragment key={comment.id}>
                              <Box
                                ref={index === comments.length - 1 ? commentsEndRef : null}
                                sx={{
                                  mb: 1,
                                }}
                              >
                                {/* Author and timestamp header */}
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  mb={1}
                                >
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Box
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        backgroundColor: colors.primary.main,
                                        color: '#ffffff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                      }}
                                    >
                                      {comment.author_name.charAt(0).toUpperCase()}
                                    </Box>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        fontWeight: 600,
                                        color: '#000000',
                                        fontSize: '0.875rem',
                                      }}
                                    >
                                      {comment.author_name}
                                    </Typography>
                                  </Box>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: '#737B8B',
                                        fontSize: '0.75rem',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {new Date(comment.created_at).toLocaleString('en-US', {
                                        weekday: 'short',
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}
                                    </Typography>
                                    {canEdit &&
                                      (user.role === 'ADMIN' || comment.author_id === user.id) && (
                                        <Tooltip title="Delete comment">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleDeleteComment(comment.id)}
                                            sx={{
                                              color: colors.text.primary,
                                              '&:hover': {
                                                color: colors.error.main,
                                              },
                                            }}
                                          >
                                            <FiTrash2 size={16} />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                  </Box>
                                </Box>

                                {/* Comment text */}
                                <Box
                                  sx={{
                                    p: '10px',
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: colors.text.primary,
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                      lineHeight: 1.5,
                                      fontSize: '0.9rem',
                                      fontWeight: 500,
                                      backgroundColor: '#F7FAFC',
                                      p: 1.5,
                                      borderRadius: '4px',
                                      display: 'inline-block',
                                      maxWidth: '100%',
                                    }}
                                  >
                                    {comment.comment_text}
                                  </Typography>
                                </Box>
                              </Box>
                              {index < comments.length - 1 && <Divider sx={{ mb: 2 }} />}
                            </React.Fragment>
                          ))}
                        </Box>
                      )}
                    </Box>

                    {/* Comment Input */}
                    {canEdit && (
                      <Box
                        sx={{
                          mb: 0.5,
                          p: 1.5,
                          borderTop: '1px solid #e5e7eb',
                          backgroundColor: colors.background.sidebar,
                        }}
                      >
                        <Box display="flex" gap={1} alignItems="flex-start">
                          <Box sx={{ flex: 1, position: 'relative' }}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={1}
                              maxRows={3}
                              placeholder="Add a comment..."
                              value={newComment}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value.length <= 2000) {
                                  setNewComment(value);
                                }
                              }}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  if (newComment.trim() && !submittingComment) {
                                    handleAddComment();
                                  }
                                }
                              }}
                              inputProps={{
                                maxLength: 2000,
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  padding: '8px',
                                  borderRadius: '4px',
                                  backgroundColor: '#ffffff',
                                },
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                position: 'absolute',
                                bottom: '-20px',
                                right: 0,
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                              }}
                            >
                              {newComment.length}/2000
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            onClick={handleAddComment}
                            disabled={!newComment.trim() || submittingComment}
                            startIcon={
                              submittingComment ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <Send />
                              )
                            }
                            sx={{
                              backgroundColor: colors.primary.main,
                              color: colors.secondary.contrastText,
                              textTransform: 'none',
                              borderRadius: '4px',
                              fontWeight: 600,
                              px: 2,
                              minWidth: '80px',
                              height: '40px',
                              mt: 0,
                              '&:hover': {
                                backgroundColor: colors.primary.dark,
                              },
                              '&:disabled': {
                                backgroundColor: colors.action.row,
                                color: colors.text.disabled,
                              },
                            }}
                          >
                            {submittingComment ? 'Posting...' : 'Post'}
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </Card>
            </Grid>
          </Grid>
        </Box>
      ) : (
        // Edit Mode
        <Box>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <IconButton
                onClick={() => navigate('/reminders')}
                size="small"
                sx={{
                  '&:hover': { color: 'rgba(0, 0, 0, 1)' },
                }}
              >
                <ArrowBack fontSize="small" />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: '#000000',
                }}
              >
                Edit Reminder
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={handleCancelEdit}
                disabled={saving}
                sx={{
                  borderRadius: '4px',
                  textTransform: 'none',
                  borderColor: colors.text.secondary,
                  color: colors.text.secondary,
                  '&:hover': {
                    borderColor: colors.text.primary,
                    backgroundColor: colors.action.hover,
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveClick}
                disabled={saving}
                sx={{
                  borderRadius: '4px',
                  textTransform: 'none',
                  backgroundColor: colors.primary.main,
                  '&:hover': {
                    backgroundColor: colors.primary.dark,
                  },
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Box>

          {/* Edit Form */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: '4px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  p: 3,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: colors.primary.main,
                    mb: 2,
                    pb: 1,
                    borderBottom: `2px solid ${colors.primary.main}`,
                  }}
                >
                  Reminder Information
                </Typography>

                <Grid container spacing={3}>
                  {/* Left Column */}
                  <Grid item xs={12} md={6}>
                    <Box display="flex" flexDirection="column" gap={2.5}>
                      {/* Contact - Read Only */}
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            fontSize: '0.7rem',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Contact
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.text.primary,
                            fontWeight: 500,
                            mt: 0.5,
                          }}
                        >
                          {getContactName()}
                        </Typography>
                      </Box>

                      {/* Email - Read Only */}
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            fontSize: '0.7rem',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Email
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.text.primary,
                            fontWeight: 500,
                            mt: 0.5,
                          }}
                        >
                          {getDisplayValue(reminder.primary_email)}
                        </Typography>
                      </Box>

                      {/* Phone Number - Read Only */}
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            fontSize: '0.7rem',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Phone Number
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.text.primary,
                            fontWeight: 500,
                            mt: 0.5,
                          }}
                        >
                          {getDisplayValue(reminder.primary_number)}
                        </Typography>
                      </Box>

                      {/* Amount - Read Only */}
                      {reminder.amount && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Amount
                          </Typography>
                          <Box display="flex" alignItems="baseline" gap={1} mt={0.5}>
                            <Typography
                              variant="h5"
                              sx={{
                                color: colors.text.primary,
                                fontWeight: 700,
                              }}
                            >
                              {getDisplayValue(reminder.amount, 'currency')}
                            </Typography>
                            {reminder.collected_amount !== undefined && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: colors.text.secondary,
                                  fontWeight: 500,
                                }}
                              >
                                (Collected: {getDisplayValue(reminder.collected_amount, 'currency')}
                                )
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}

                      {/* Donation Schema - Read Only */}
                      {reminder.donation_schema_name && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Donation Scheme
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.text.primary,
                              fontWeight: 600,
                              mt: 0.5,
                            }}
                          >
                            {reminder.donation_schema_name}
                          </Typography>
                        </Box>
                      )}

                      {/* Purpose - Read Only */}
                      {reminder.purpose && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Purpose
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.text.primary,
                              mt: 0.5,
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {reminder.purpose}
                          </Typography>
                        </Box>
                      )}

                      {/* Notes - Read Only */}
                      {reminder.notes && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Notes
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.text.primary,
                              mt: 0.5,
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {reminder.notes}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  {/* Right Column */}
                  <Grid item xs={12} md={6}>
                    <Box display="flex" flexDirection="column" gap={2.5}>
                      {/* Frequency - Read Only */}
                      {reminder.frequency && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Frequency
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.text.primary,
                              fontWeight: 500,
                              mt: 0.5,
                            }}
                          >
                            {getFrequencyLabel(reminder.frequency)}
                          </Typography>
                        </Box>
                      )}

                      {/* Status - EDITABLE */}
                      <Box>
                        {/* Amount Collection Info */}
                        {reminder.reminder_amount && reminder.reminder_amount > 0 && (
                          <Box
                            sx={{
                              mb: 2,
                              p: 2,
                              borderRadius: '4px',
                              backgroundColor:
                                (reminder.collected_amount || 0) >= reminder.reminder_amount
                                  ? `${colors.success.main}10`
                                  : `${colors.warning.main}10`,
                              border: `1px solid ${
                                (reminder.collected_amount || 0) >= reminder.reminder_amount
                                  ? colors.success.main
                                  : colors.warning.main
                              }`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                color:
                                  (reminder.collected_amount || 0) >= reminder.reminder_amount
                                    ? colors.success.main
                                    : colors.warning.main,
                                display: 'block',
                                mb: 0.5,
                              }}
                            >
                              Payment Status
                            </Typography>
                            <Typography variant="body2" sx={{ color: colors.text.primary }}>
                              Collected: ₹
                              {parseFloat(reminder.collected_amount || 0).toLocaleString()} / ₹
                              {parseFloat(reminder.reminder_amount).toLocaleString()}
                            </Typography>
                            {(reminder.collected_amount || 0) < reminder.reminder_amount && (
                              <Typography
                                variant="caption"
                                sx={{ color: colors.text.secondary, display: 'block', mt: 0.5 }}
                              >
                                Remaining: ₹
                                {parseFloat(
                                  reminder.reminder_amount - (reminder.collected_amount || 0),
                                ).toLocaleString()}
                              </Typography>
                            )}
                          </Box>
                        )}

                        <TextField
                          select
                          fullWidth
                          required
                          label="Status"
                          name="recurring_status"
                          value={editFormData.recurring_status}
                          onChange={handleChange}
                          error={!!fieldErrors.recurring_status}
                          helperText={
                            fieldErrors.recurring_status ||
                            ((reminder.collected_amount || 0) >= (reminder.reminder_amount || 0)
                              ? 'Updating this will also update the donation'
                              : 'Full amount must be collected before marking as completed')
                          }
                          FormHelperTextProps={{
                            sx: { marginLeft: 0 },
                          }}
                          SelectProps={{
                            MenuProps: {
                              PaperProps: {
                                sx: {
                                  borderRadius: 0,
                                },
                              },
                              sx: {
                                '& .MuiMenu-list': {
                                  padding: '0px',
                                },
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '4px',
                            },
                          }}
                        >
                          <MenuItem
                            value="PENDING"
                            sx={{
                              p: '8px 16px',
                              minHeight: '40px',
                              '&:hover': { borderRadius: 0 },
                              '&.Mui-selected': { borderRadius: 0 },
                            }}
                          >
                            Pending
                          </MenuItem>
                          <MenuItem
                            value="COMPLETED"
                            sx={{
                              p: '8px 16px',
                              minHeight: '40px',
                              '&:hover': { borderRadius: 0 },
                              '&.Mui-selected': { borderRadius: 0 },
                            }}
                          >
                            Completed
                          </MenuItem>
                        </TextField>
                      </Box>

                      {/* Reminder Date - EDITABLE */}
                      <Box>
                        <TextField
                          fullWidth
                          required
                          type="date"
                          label="Reminder Date"
                          name="reminder_date"
                          value={editFormData.reminder_date}
                          onChange={handleChange}
                          error={!!fieldErrors.reminder_date}
                          helperText={
                            fieldErrors.reminder_date ||
                            "Editing this date won't affect future recurring reminders"
                          }
                          FormHelperTextProps={{
                            sx: { marginLeft: 0 },
                          }}
                          InputLabelProps={{ shrink: true }}
                          onClick={(e) => {
                            e.target.focus();
                            setTimeout(() => e.target.showPicker?.(), 0);
                          }}
                          sx={{
                            cursor: 'pointer',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '4px',
                            },
                          }}
                        />
                      </Box>

                      {/* Next Recurring Date - EDITABLE */}
                      {reminder.next_pledge_date && (
                        <Box>
                          <TextField
                            fullWidth
                            required
                            type="date"
                            label="Next Pledge Date"
                            name="next_recurring_date"
                            value={editFormData.next_recurring_date}
                            onChange={handleChange}
                            error={!!fieldErrors.next_recurring_date}
                            helperText={
                              fieldErrors.next_recurring_date ||
                              'When the next reminder is scheduled'
                            }
                            FormHelperTextProps={{
                              sx: { marginLeft: 0 },
                            }}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                              min: getTodayDate(),
                              max: editFormData.pledge_end_date || undefined,
                            }}
                            onClick={(e) => {
                              e.target.focus();
                              setTimeout(() => e.target.showPicker?.(), 0);
                            }}
                            sx={{
                              cursor: 'pointer',
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                              },
                            }}
                          />
                        </Box>
                      )}

                      {/* Recurring End Date - EDITABLE (if exists) */}
                      {(reminder.recurring_end_date || editFormData.recurring_end_date) && (
                        <Box>
                          <TextField
                            fullWidth
                            type="date"
                            label="Recurring End Date"
                            name="recurring_end_date"
                            value={editFormData.recurring_end_date}
                            onChange={handleChange}
                            error={!!fieldErrors.recurring_end_date}
                            helperText={
                              fieldErrors.recurring_end_date ||
                              'When reminder should stop (optional)'
                            }
                            FormHelperTextProps={{
                              sx: { marginLeft: 0 },
                            }}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                              min: getTodayDate(),
                            }}
                            onClick={(e) => {
                              e.target.focus();
                              setTimeout(() => e.target.showPicker?.(), 0);
                            }}
                            sx={{
                              cursor: 'pointer',
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                              },
                            }}
                          />
                        </Box>
                      )}

                      {/* Pledge End Date - READ ONLY */}
                      {(reminder.pledge_end_date || editFormData.pledge_end_date) && (
                        <Box>
                          <TextField
                            fullWidth
                            type="date"
                            label="Pledge End Date"
                            name="pledge_end_date"
                            value={editFormData.pledge_end_date}
                            disabled
                            error={!!fieldErrors.pledge_end_date}
                            helperText={fieldErrors.pledge_end_date || 'When the pledge should end'}
                            FormHelperTextProps={{
                              sx: { marginLeft: 0 },
                            }}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                              },
                            }}
                          />
                        </Box>
                      )}

                      {/* Campaign - Read Only */}
                      {reminder.campaign_name && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Campaign
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.text.primary,
                              fontWeight: 500,
                              mt: 0.5,
                            }}
                          >
                            {reminder.campaign_name}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>

                {/* Info Alert */}
                <Alert severity="info" sx={{ mt: 3 }}>
                  Note: Updating the reminder date and recurring status will also update the
                  associated donation pledge. Pledge end date cannot be edited from reminders.
                </Alert>
              </Card>
            </Grid>

            {/* Comments Section */}
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: '4px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  height: '600px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ p: 3, pb: 0 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: colors.primary.main,
                      mb: 2,
                      pb: 1,
                      borderBottom: `2px solid ${colors.primary.main}`,
                    }}
                  >
                    Comments
                  </Typography>
                </Box>

                {commentsLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
                    <CircularProgress />
                  </Box>
                ) : commentsError ? (
                  <Box p={3}>
                    <Alert
                      severity="error"
                      action={
                        <IconButton size="small" onClick={() => loadComments(reminder?.contact_id)}>
                          <Refresh />
                        </IconButton>
                      }
                    >
                      {commentsError}
                    </Alert>
                  </Box>
                ) : (
                  <>
                    {/* Comments Feed */}
                    <Box
                      sx={{
                        flex: 1,
                        overflowY: 'auto',
                        p: 3,
                        backgroundColor: '#ffffff',
                      }}
                    >
                      {comments.length === 0 ? (
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                          height="100%"
                        >
                          <CommentIcon sx={{ fontSize: 64, color: '#ddd', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                            No comments yet
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Start the conversation by adding a comment below
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          {comments.map((comment, index) => (
                            <React.Fragment key={comment.id}>
                              <Box
                                ref={index === comments.length - 1 ? commentsEndRef : null}
                                sx={{
                                  mb: 1,
                                }}
                              >
                                {/* Author and timestamp header */}
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  mb={1}
                                >
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Box
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        backgroundColor: colors.primary.main,
                                        color: '#ffffff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                      }}
                                    >
                                      {comment.author_name.charAt(0).toUpperCase()}
                                    </Box>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        fontWeight: 600,
                                        color: '#000000',
                                        fontSize: '0.875rem',
                                      }}
                                    >
                                      {comment.author_name}
                                    </Typography>
                                  </Box>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: '#737B8B',
                                        fontSize: '0.75rem',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {new Date(comment.created_at).toLocaleString('en-US', {
                                        weekday: 'short',
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}
                                    </Typography>
                                    {canEdit &&
                                      (user.role === 'ADMIN' || comment.author_id === user.id) && (
                                        <Tooltip title="Delete comment">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleDeleteComment(comment.id)}
                                            sx={{
                                              color: colors.text.primary,
                                              '&:hover': {
                                                color: colors.error.main,
                                              },
                                            }}
                                          >
                                            <FiTrash2 size={16} />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                  </Box>
                                </Box>

                                {/* Comment text */}
                                <Box
                                  sx={{
                                    p: '10px',
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: colors.text.primary,
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                      lineHeight: 1.5,
                                      fontSize: '0.9rem',
                                      fontWeight: 500,
                                      backgroundColor: '#F7FAFC',
                                      p: 1.5,
                                      borderRadius: '4px',
                                      display: 'inline-block',
                                      maxWidth: '100%',
                                    }}
                                  >
                                    {comment.comment_text}
                                  </Typography>
                                </Box>
                              </Box>
                              {index < comments.length - 1 && <Divider sx={{ mb: 2 }} />}
                            </React.Fragment>
                          ))}
                        </Box>
                      )}
                    </Box>

                    {/* Comment Input */}
                    {canEdit && (
                      <Box
                        sx={{
                          mb: 0.5,
                          p: 1.5,
                          borderTop: '1px solid #e5e7eb',
                          backgroundColor: colors.background.sidebar,
                        }}
                      >
                        <Box display="flex" gap={1} alignItems="flex-start">
                          <Box sx={{ flex: 1, position: 'relative' }}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={1}
                              maxRows={3}
                              placeholder="Add a comment..."
                              value={newComment}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value.length <= 2000) {
                                  setNewComment(value);
                                }
                              }}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  if (newComment.trim() && !submittingComment) {
                                    handleAddComment();
                                  }
                                }
                              }}
                              inputProps={{
                                maxLength: 2000,
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  padding: '8px',
                                  borderRadius: '4px',
                                  backgroundColor: '#ffffff',
                                },
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                position: 'absolute',
                                bottom: '-20px',
                                right: 0,
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                              }}
                            >
                              {newComment.length}/2000
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            onClick={handleAddComment}
                            disabled={!newComment.trim() || submittingComment}
                            startIcon={
                              submittingComment ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <Send />
                              )
                            }
                            sx={{
                              backgroundColor: colors.primary.main,
                              color: colors.secondary.contrastText,
                              textTransform: 'none',
                              borderRadius: '4px',
                              fontWeight: 600,
                              px: 2,
                              minWidth: '80px',
                              height: '40px',
                              mt: 0,
                              '&:hover': {
                                backgroundColor: colors.primary.dark,
                              },
                              '&:disabled': {
                                backgroundColor: colors.action.row,
                                color: colors.text.disabled,
                              },
                            }}
                          >
                            {submittingComment ? 'Posting...' : 'Post'}
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Donation Confirmation Dialog */}
      <DonationConfirmationWrapper
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleSave}
        title="Update Reminder and Donation"
        message="You are about to update this reminder. This will also update the associated donation record."
        warningMessage="The donation's next pledge date, pledge end date, and status will be updated to match the reminder changes."
        loading={saving}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemName="reminder"
        deleting={deleting}
      />
    </Box>
  );
}
